import { type FC, useEffect, useRef, useState } from 'react';

import {
  INACTIVITY_LIMIT_MS,
  markActivity,
  readLastActivity,
  WARNING_COUNTDOWN_MS,
  WARNING_COUNTDOWN_SECONDS,
} from '@features/authentication/utils/inactivity';
import { GenericModal } from '@shared/components/ui/custom';

const secondsUntilLogout = (deadline: number): number =>
  Math.max(0, Math.min(WARNING_COUNTDOWN_SECONDS, Math.ceil((deadline - Date.now()) / 1000)));

export const InactivityLogoutModal: FC<{ onClose: () => void }> = ({ onClose }) => {
  const lastActivity = readLastActivity();

  const initialDeadline = lastActivity
    ? lastActivity + INACTIVITY_LIMIT_MS + WARNING_COUNTDOWN_MS
    : Date.now() + WARNING_COUNTDOWN_MS;

  const deadlineRef = useRef<number>(initialDeadline);
  const [countdown, setCountdown] = useState(() => secondsUntilLogout(deadlineRef.current));

  useEffect(() => {
    const interval = setInterval(() => setCountdown(secondsUntilLogout(deadlineRef.current)), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleDismiss = () => {
    // An explicit dismissal is the strongest "I'm still here" signal — reset the shared clock so the
    // monitor (this and every other tab) stops counting down.
    markActivity();
    onClose();
  };

  return (
    <GenericModal
      open
      onOpenChange={handleDismiss}
      title="Are you still there?"
      description={
        <>
          You will be logged out in{' '}
          <span className="font-semibold" data-testid="inactivity-logout-countdown">
            {countdown}
          </span>{' '}
          seconds due to inactivity.
        </>
      }
      data-testid="inactivity-logout-modal"
    />
  );
};
